// src/pages/UserManagement.js - FINAL VERSION WITH AVATARS
import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Card,
  CardContent,
  Avatar,
  Divider,
  alpha,
  useTheme,
  useMediaQuery,
  Pagination,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import PeopleIcon from '@mui/icons-material/People';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import userService from '../services/userService';
import { getPasswordPolicyError, PASSWORD_POLICY_HELPER } from '../utils/passwordPolicy';

// System Roles Configuration
const ROLES = {
  admin: {
    label: 'Administrator',
    color: '#d32f2f',
    icon: AdminPanelSettingsIcon,
    description: 'Full system access',
  },
  manager: {
    label: 'Manager',
    color: '#ed6c02',
    icon: VerifiedUserIcon,
    description: 'Project and team management',
  },
  user: {
    label: 'User',
    color: '#1976d2',
    icon: PersonIcon,
    description: 'Access to assigned projects',
  },
  client: {
    label: 'Client',
    color: '#00897b',
    icon: BusinessIcon,
    description: 'Limited view of their projects',
  },
  viewer: {
    label: 'Viewer',
    color: '#2e7d32',
    icon: BusinessIcon,
    description: 'Limited view of their projects',
  },
};

// System Permissions Definition
const PERMISSIONS = [
  { id: 'projects_view', label: 'View Projects', module: 'Projects' },
  { id: 'projects_create', label: 'Create Projects', module: 'Projects' },
  { id: 'projects_edit', label: 'Edit Projects', module: 'Projects' },
  { id: 'projects_delete', label: 'Delete Projects', module: 'Projects' },
  { id: 'clients_view', label: 'View Clients', module: 'Clients' },
  { id: 'clients_create', label: 'Create Clients', module: 'Clients' },
  { id: 'clients_edit', label: 'Edit Clients', module: 'Clients' },
  { id: 'tasks_view', label: 'View Tasks', module: 'Tasks' },
  { id: 'tasks_create', label: 'Create Tasks', module: 'Tasks' },
  { id: 'users_view', label: 'View Users', module: 'Users' },
  { id: 'users_create', label: 'Create Users', module: 'Users' },
  { id: 'reports_view', label: 'View Reports', module: 'Reports' },
];

// Permissions Mapping by Role
const ROLE_PERMISSIONS = {
  admin: PERMISSIONS.map((p) => p.id),
  manager: ['projects_view', 'projects_create', 'projects_edit', 'clients_view', 'clients_create', 'tasks_view', 'tasks_create', 'reports_view'],
  user: ['projects_view', 'clients_view', 'tasks_view', 'tasks_create'],
  client: ['projects_view', 'tasks_view'],
  viewer: ['projects_view', 'tasks_view'],
};

const INITIAL_FORM_STATE = {
  username: '',
  email: '',
  password: '',
  first_name: '',
  last_name: '',
  role: 'user',
};

// ✅ Helper para construir la URL correcta de la imagen
const getAvatarUrl = (path) => {
    if (!path) return null;
    // Si ya empieza con http (es una url externa), la dejamos igual
    // Si no, le pegamos la URL de nuestro backend
    return path.startsWith('http') 
        ? path 
        : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${path}`;
};

export default function UserManagement() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // States
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Filters & Pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('');
  const [filterStatus, setFilterStatus] = useState(''); 
  const [page, setPage] = useState(1);
  const itemsPerPage = 6;

  // Dialogs & Selection
  const [selectedUser, setSelectedUser] = useState(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openPermissions, setOpenPermissions] = useState(false);
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  
  // Forms
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);

  // Load Data
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await userService.getUsers();
      
      const transformedUsers = (response.users || []).map((u) => {
        const isActive = u.is_active === 1 || u.is_active === true || u.status === 'active';
        
        return {
          id: u.id,
          username: u.username,
          email: u.email,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          name: (u.first_name && u.last_name) ? `${u.first_name} ${u.last_name}` : u.username,
          role: u.role || 'user',
          status: isActive ? 'active' : 'inactive',
          avatar: u.avatar, // ✅ Importante: Asegurar que mapeamos el avatar
          createdAt: u.created_at,
          lastLogin: u.updated_at
        };
      });

      setUsers(transformedUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Error loading users: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter Logic
  const filtered = useMemo(() => {
    return users.filter((user) => {
      const matchSearch = searchQuery
        ? user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.username.toLowerCase().includes(searchQuery.toLowerCase())
        : true;
      const matchRole = filterRole ? user.role === filterRole : true;
      const matchStatus = filterStatus ? user.status === filterStatus : true;
      return matchSearch && matchRole && matchStatus;
    });
  }, [users, searchQuery, filterRole, filterStatus]);

  // Pagination Logic
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return filtered.slice(startIndex, startIndex + itemsPerPage);
  }, [filtered, page]);

  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // Stats Logic
  const stats = useMemo(
    () => ({
      total: users.length,
      active: users.filter((u) => u.status === 'active').length,
      admins: users.filter((u) => u.role === 'admin').length,
      viewers: users.filter((u) => u.role === 'viewer' || u.role === 'client').length,
    }),
    [users]
  );

  // Handlers
  const handleOpenCreateDialog = () => {
    setFormData(INITIAL_FORM_STATE);
    setOpenCreateDialog(true);
  };

  const handleCreateUser = async () => {
    try {
      if (!formData.username || !formData.email || !formData.password) {
        setError('Username, email, and password are required');
        return;
      }
      const passwordPolicyError = getPasswordPolicyError(formData.password, formData);
      if (passwordPolicyError) {
        setError(`Password policy: ${passwordPolicyError}`);
        return;
      }

      await userService.createUser(formData);
      
      setSuccess('✅ User created successfully');
      setOpenCreateDialog(false);
      setFormData(INITIAL_FORM_STATE);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error creating user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: '',
      first_name: user.first_name,
      last_name: user.last_name,
      role: user.role,
    });
    setOpenEditDialog(true);
  };

  const handleSaveUser = async () => {
    try {
      if (!formData.email) {
        setError('Email is required');
        return;
      }

      const updateData = { ...formData };
      if (!updateData.password || updateData.password.trim() === '') {
        delete updateData.password;
      } else {
        const passwordPolicyError = getPasswordPolicyError(updateData.password, {
          username: selectedUser.username,
          email: updateData.email
        });
        if (passwordPolicyError) {
          setError(`Password policy: ${passwordPolicyError}`);
          return;
        }
      }

      await userService.updateUser(selectedUser.id, updateData);
      
      setSuccess('✅ User updated successfully');
      setOpenEditDialog(false);
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error updating user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;

    try {
      await userService.deleteUser(userId);
      setSuccess('✅ User deleted successfully');
      if (paginatedUsers.length === 1 && page > 1) {
        setPage(prev => prev - 1);
      }
      loadUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Error deleting user: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleViewPermissions = (user) => {
    setSelectedUser(user);
    setSelectedPermissions(ROLE_PERMISSIONS[user.role] || []);
    setOpenPermissions(true);
  };

  const getAvatarColor = (name) => {
    const colors = ['#1976d2', '#2e7d32', '#d32f2f', '#ed6c02', '#9c27b0'];
    const index = name ? (name.charCodeAt(0) % colors.length) : 0;
    return colors[index];
  };

  // --- Components ---

  const UserCard = ({ user }) => {
    const roleInfo = ROLES[user.role] || ROLES.user;
    const RoleIcon = roleInfo.icon;

    return (
      <Card
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          transition: 'all 0.3s ease',
          '@media (hover: hover)': {
            '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
          },
        }}
      >
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', gap: { xs: 1.25, sm: 2 }, alignItems: 'flex-start', mb: 2 }}>
            {/* ✅ Avatar con imagen real */}
            <Avatar 
                src={getAvatarUrl(user.avatar)} 
                sx={{ bgcolor: getAvatarColor(user.name), width: 56, height: 56 }}
            >
              {!user.avatar && user.name.substring(0, 2).toUpperCase()}
            </Avatar>
            
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 0.75, mb: 0.5 }}>
                <Typography variant="h6" sx={{ minWidth: 0, flex: '1 1 130px', fontWeight: 700, fontSize: '1rem', overflowWrap: 'anywhere' }}>
                  {user.name}
                </Typography>
                <Chip
                  icon={user.status === 'active' ? <CheckCircleIcon /> : <BlockIcon />}
                  label={user.status === 'active' ? 'Active' : 'Inactive'}
                  size="small"
                  color={user.status === 'active' ? 'success' : 'default'}
                  sx={{ fontSize: '0.7rem', height: 24 }}
                />
              </Box>
              <Chip
                icon={<RoleIcon sx={{ fontSize: 14 }} />}
                label={roleInfo.label}
                size="small"
                sx={{
                  bgcolor: alpha(roleInfo.color, 0.1),
                  color: roleInfo.color,
                  fontSize: '0.7rem',
                  height: 22,
                  mb: 1,
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem', wordBreak: 'break-all' }}>
                  {user.email}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                  Joined: {user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-US') : 'Unknown'}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) 44px', gap: 1, mt: 2 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={() => handleEditUser(user)}
              fullWidth
            >
              Edit
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<VerifiedUserIcon />}
              onClick={() => handleViewPermissions(user)}
              fullWidth
            >
              Perms
            </Button>
            <IconButton aria-label={`Delete ${user.name}`} color="error" onClick={() => handleDeleteUser(user.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading && users.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.35, md: 1.6 },
          mb: 1.5,
          borderRadius: 2.5,
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <Box sx={{ bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 2, p: 0.8, display: 'flex' }}>
              <PeopleIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: '1.05rem', md: '1.3rem' } }}>
                User Management
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Total: {stats.total} registered users
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            size="small"
            sx={{ width: { xs: '100%', sm: 'auto' }, bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
          >
            New User
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={1.2} sx={{ mb: 1.5 }}>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Total Users</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#1976d2' }}>{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Active</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32' }}>{stats.active}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Admins</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#d32f2f' }}>{stats.admins}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center', py: 1.2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Viewers</Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: '#ed6c02' }}>{stats.viewers}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Bar */}
      <Paper elevation={0} sx={{ p: 1.3, mb: 1.5, borderRadius: 2.5, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'minmax(220px, 1fr) 150px 150px' }, gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name, email or username..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
            sx={{ minWidth: 0 }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            }}
          />
          <TextField
            size="small"
            select
            label="Role"
            value={filterRole}
            onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}
            sx={{ minWidth: 0 }}
          >
            <MenuItem value="">All Roles</MenuItem>
            {Object.entries(ROLES).map(([key, value]) => (
              <MenuItem key={key} value={key}>{value.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            select
            label="Status"
            value={filterStatus}
            onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
            sx={{ minWidth: 0 }}
          >
            <MenuItem value="">All Status</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {/* User Grid */}
      {paginatedUsers.length === 0 ? (
        <Paper elevation={0} sx={{ p: 2.5, textAlign: 'center', border: '1px dashed', borderColor: 'divider', borderRadius: 2.5 }}>
          <Typography variant="body1" color="text.secondary">No users found matching your filters.</Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.2}>
          {paginatedUsers.map((user) => (
            <Grid item xs={12} sm={6} lg={4} key={user.id}>
              <UserCard user={user} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            color="primary"
            size={isMobile ? 'medium' : 'large'}
            showFirstButton={!isMobile}
            showLastButton={!isMobile}
          />
        </Box>
      )}

      {/* --- DIALOGS --- */}

      {/* Dialog - Create User */}
      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>Create New User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              value={formData.username}
              onChange={(e) => setFormData({ ...formData, username: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              required
              helperText={PASSWORD_POLICY_HELPER}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                {Object.entries(ROLES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - Edit User */}
      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Username"
              value={formData.username}
              fullWidth
              disabled
            />
            <TextField
              label="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              fullWidth
            />
            <TextField
              label="Password (Leave blank to keep current)"
              type="password"
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              fullWidth
              helperText={`Only fill to reset it. ${PASSWORD_POLICY_HELPER}`}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="First Name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Last Name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                fullWidth
              />
            </Stack>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                label="Role"
              >
                {Object.entries(ROLES).map(([key, value]) => (
                  <MenuItem key={key} value={key}>{value.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEditDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveUser} variant="contained">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - Permissions */}
      <Dialog open={openPermissions} onClose={() => setOpenPermissions(false)} maxWidth="md" fullWidth fullScreen={isMobile}>
        <DialogTitle>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
          <span>Permissions for {selectedUser?.name}</span>
          {selectedUser && (
            <Chip
              label={ROLES[selectedUser.role]?.label || 'User'}
              size="small"
              sx={{
                bgcolor: alpha(ROLES[selectedUser.role]?.color || '#1976d2', 0.1),
                color: ROLES[selectedUser.role]?.color || '#1976d2',
                alignSelf: 'flex-start',
              }}
            />
          )}
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            These permissions are defined by the user's role ({ROLES[selectedUser?.role]?.label}).
          </Typography>
          {['Projects', 'Clients', 'Tasks', 'Users', 'Reports'].map((module) => (
            <Box key={module} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>{module}</Typography>
              <List dense>
                {PERMISSIONS.filter((p) => p.module === module).map((permission) => (
                  <ListItem key={permission.id} disablePadding>
                    <ListItemIcon>
                      <Checkbox disabled checked={selectedPermissions.includes(permission.id)} />
                    </ListItemIcon>
                    <ListItemText primary={permission.label} />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPermissions(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
